/* eslint-disable ember/no-observers */
import { tracked } from "@glimmer/tracking";
import Controller, { inject as controller } from "@ember/controller";
import { action, computed } from "@ember/object";
import { dependentKeyCompat } from "@ember/object/compat";
import { service } from "@ember/service";
import { isEmpty } from "@ember/utils";
import { observes } from "@ember-decorators/object";
import { Promise } from "rsvp";
import { ajax } from "discourse/lib/ajax";
import { addUniqueValuesToArray } from "discourse/lib/array-tools";
import { search as searchCategoryTag } from "discourse/lib/category-tag-search";
import { bind } from "discourse/lib/decorators";
import { setTransient } from "discourse/lib/page-tracker";
import PostBulkSelectHelper from "discourse/lib/post-bulk-select-helper";
import { scrollTop } from "discourse/lib/scroll-top";
import {
  getSearchKey,
  isValidSearchTerm,
  logSearchLinkClick,
  reciprocallyRankedList,
  searchContextDescription,
  searchTermScopesToPMs,
  translateResults,
  updateRecentSearches,
} from "discourse/lib/search";
import { applyBehaviorTransformer } from "discourse/lib/transformer";
import userSearch from "discourse/lib/user-search";
import { escapeExpression } from "discourse/lib/utilities";
import Category from "discourse/models/category";
import Composer from "discourse/models/composer";
import { i18n } from "discourse-i18n";

export const SEARCH_TYPE_DEFAULT = "topics_posts";
export const SEARCH_TYPE_CATS_TAGS = "categories_tags";
export const SEARCH_TYPE_USERS = "users";

const PAGE_LIMIT = 10;

const customSearchTypes = [];

export function registerFullPageSearchType(
  translationKey,
  searchTypeId,
  searchFunc
) {
  customSearchTypes.push({ translationKey, searchTypeId, searchFunc });
}

export default class FullPageSearchController extends Controller {
  @service composer;

  @service appEvents;

  @service siteSettings;

  @service searchPreferencesManager;

  @service currentUser;

  @controller application;

  @tracked searching = false;
  @tracked loading = false;

  bulkSelectEnabled = null;

  queryParams = [
    "q",
    "expanded",
    "context_id",
    "context",
    "skip_context",
    "search_type",
  ];

  q;
  context_id = null;
  search_type = SEARCH_TYPE_DEFAULT;
  context = null;
  sortOrder = 0;
  sortOrders = null;
  invalidSearch = false;
  page = 1;
  resultCount = null;
  searchTypes = null;
  additionalSearchResults = [];
  error = null;
  _searchOnSortChange = true;

  init() {
    super.init(...arguments);

    this.set(
      "sortOrder",
      this.searchPreferencesManager.sortOrder ||
        this.siteSettings.search_default_sort_order
    );

    const searchTypes = [
      { name: i18n("search.type.default"), id: SEARCH_TYPE_DEFAULT },
      {
        name: this.siteSettings.tagging_enabled
          ? i18n("search.type.categories_and_tags")
          : i18n("search.type.categories"),
        id: SEARCH_TYPE_CATS_TAGS,
      },
      { name: i18n("search.type.users"), id: SEARCH_TYPE_USERS },
    ];

    customSearchTypes.forEach((type) => {
      searchTypes.push({
        name: i18n(type.translationKey),
        id: type.searchTypeId,
      });
    });

    this.set("searchTypes", searchTypes);

    this.sortOrders = [
      { name: i18n("search.relevance"), id: 0 },
      {
        name: i18n("search.latest_post"),
        id: 1,
        term: "order:latest",
        alias: "l",
      },
      { name: i18n("search.most_liked"), id: 2, term: "order:likes" },
      { name: i18n("search.most_viewed"), id: 3, term: "order:views" },
      {
        name: i18n("search.latest_topic"),
        id: 4,
        term: "order:latest_topic",
      },
    ];

    if (this.currentUser) {
      this.sortOrders.push({
        name: i18n("search.last_read"),
        id: 5,
        term: "order:read",
        alias: "r",
      });
    }

    this.bulkSelectHelper = new PostBulkSelectHelper(this);
  }

  @computed("bulkSelectHelper.selected.length")
  get hasSelection() {
    return this.bulkSelectHelper?.selected?.length > 0;
  }

  @dependentKeyCompat
  get searchButtonDisabled() {
    return this.searching || this.loading;
  }

  @computed("resultCount")
  get hasResults() {
    return (this.resultCount || 0) > 0;
  }

  @computed("expanded")
  get expandFilters() {
    return this.expanded === "true";
  }

  @computed("q")
  get hasAutofocus() {
    return isEmpty(this.q);
  }

  @computed("q")
  get highlightQuery() {
    if (!this.q) {
      return;
    }
    return this.q
      .split(/\s+/)
      .filter((t) => t !== "l")
      .join(" ");
  }

  @computed("skip_context", "context")
  get searchContextEnabled() {
    return (
      (!this.skip_context && this.context) || this.skip_context === "false"
    );
  }

  set searchContextEnabled(val) {
    this.set("skip_context", !val);
  }

  @computed("context", "context_id")
  get searchContextDescription() {
    let name = this.context_id;
    if (this.context === "category") {
      let category = Category.findById(this.context_id);
      if (!category) {
        return;
      }

      name = category.get("name");
    }
    return searchContextDescription(this.context, name);
  }

  @computed("q")
  get searchActive() {
    return isValidSearchTerm(this.q, this.siteSettings);
  }

  @computed("q")
  get noSortQ() {
    const q = this.cleanTerm(this.q);
    return escapeExpression(q);
  }

  @computed("canCreateTopic", "siteSettings.login_required")
  get showSuggestion() {
    return this.canCreateTopic || !this.siteSettings?.login_required;
  }

  setSearchTerm(term) {
    this._searchOnSortChange = false;
    term = this.cleanTerm(term);
    this._searchOnSortChange = true;
    this.set("searchTerm", term);
  }

  cleanTerm(term) {
    if (term) {
      this.sortOrders.forEach((order) => {
        if (order.term) {
          let word = order.term;
          let matches = term.match(new RegExp(`(^|\\s)${word}($|\\s)`));
          if (!matches && order.alias) {
            word = order.alias;
            matches = term.match(new RegExp(`(^|\\s)${word}($|\\s)`));
          }
          if (matches) {
            this.set("sortOrder", order.id);
            term = term.replace(
              new RegExp(`(^|\\s)${word}($|\\s)`, "g"),
              "$1$2"
            );
            term = term.trim();
          }
        }
      });
    }
    return term;
  }

  @observes("sortOrder")
  triggerSearch() {
    if (this._searchOnSortChange) {
      this.set("page", 1);
      this._search();
    }
  }

  @observes("search_type")
  triggerSearchOnTypeChange() {
    if (this.searchActive) {
      this.set("page", 1);
      this._search();
    }
  }

  @observes("model")
  modelChanged() {
    if (this.searchTerm !== this.q) {
      this.setSearchTerm(this.q);
    }
  }

  @computed("q")
  get showLikeCount() {
    return this.q?.includes("order:likes");
  }

  @observes("q")
  qChanged() {
    const model = this.model;
    if (model && this.get("model.q") !== this.q) {
      this.setSearchTerm(this.q);
      this.send("search");
    }
  }

  @computed("q")
  get isPrivateMessage() {
    return (
      this.q &&
      this.currentUser &&
      (this.q.includes("in:messages") ||
        this.q.includes("in:personal") ||
        this.q.includes(
          `personal_messages:${this.currentUser.get("username_lower")}`
        ))
    );
  }

  @computed("q")
  get isPMOnly() {
    return searchTermScopesToPMs(this.q);
  }

  @computed("resultCount", "noSortQ")
  get resultCountLabel() {
    const plus = this.resultCount % 50 === 0 ? "+" : "";
    return i18n("search.result_count", {
      count: this.resultCount,
      plus,
      term: this.noSortQ,
    });
  }

  @observes("model.{posts,categories,tags,users}.length", "searchResultPosts")
  resultCountChanged() {
    if (!this.model.posts) {
      return 0;
    }

    this.set(
      "resultCount",
      this.searchResultPosts.length +
        this.model.categories.length +
        this.model.tags.length +
        this.model.users.length
    );
  }

  @computed("hasResults")
  get canBulkSelect() {
    return this.currentUser && this.currentUser.staff && this.hasResults;
  }

  @computed("bulkSelectHelper.selected.length", "searchResultPosts.length")
  get hasUnselectedResults() {
    return (
      this.bulkSelectHelper?.selected?.length < this.searchResultPosts?.length
    );
  }

  @computed("model.grouped_search_result.can_create_topic")
  get canCreateTopic() {
    return (
      this.currentUser && this.model?.grouped_search_result?.can_create_topic
    );
  }

  @computed("page")
  get isLastPage() {
    return this.page === PAGE_LIMIT;
  }

  @computed("search_type")
  get usingDefaultSearchType() {
    return this.search_type === SEARCH_TYPE_DEFAULT;
  }

  @computed("search_type")
  get customSearchType() {
    return customSearchTypes.find(
      (type) => this.search_type === type["searchTypeId"]
    );
  }

  @computed("bulkSelectEnabled")
  get searchInfoClassNames() {
    return this.bulkSelectEnabled
      ? "search-info bulk-select-visible"
      : "search-info";
  }

  @computed("model.posts", "additionalSearchResults")
  get searchResultPosts() {
    if (this.additionalSearchResults?.list?.length > 0) {
      return reciprocallyRankedList(
        [this.model?.posts, this.additionalSearchResults.list],
        ["topic_id", this.additionalSearchResults.identifier]
      );
    } else {
      return this.model?.posts;
    }
  }

  @bind
  _search() {
    if (this.searching) {
      return;
    }

    this.set("invalidSearch", false);
    const searchTerm = this.searchTerm;
    // A non-zero sortOrder means user selected an order filter, which is valid even without a search term
    const hasValidSortOrder = this.sortOrder > 0;
    if (
      !hasValidSortOrder &&
      !isValidSearchTerm(searchTerm, this.siteSettings)
    ) {
      this.set("invalidSearch", true);
      return;
    }

    let args = { q: searchTerm, page: this.page };

    if (args.page === 1) {
      this.set("bulkSelectEnabled", false);

      this.bulkSelectHelper.clearAll();
      this.set("searching", true);
      scrollTop();
    } else {
      this.set("loading", true);
    }

    const sortOrder = this.sortOrder;
    if (sortOrder && this.sortOrders[sortOrder].term) {
      args.q += " " + this.sortOrders[sortOrder].term;
    }

    this.set("q", args.q);

    const skip = this.skip_context;
    if ((!skip && this.context) || skip === "false") {
      args.search_context = {
        type: this.context,
        id: this.context_id,
      };
    }

    const searchKey = getSearchKey(args);

    if (this.customSearchType) {
      const customSearch = this.customSearchType["searchFunc"];
      customSearch(this, args, searchKey);
      return;
    }

    switch (this.search_type) {
      case SEARCH_TYPE_CATS_TAGS:
        const categoryTagSearch = searchCategoryTag(
          searchTerm,
          this.siteSettings
        );
        Promise.resolve(categoryTagSearch)
          .then(async (results) => {
            const categories = results.filter((c) => Boolean(c.model));
            const tags = results.filter((c) => !c.model);
            const model = (await translateResults({ categories, tags })) || {};
            this.set("model", model);
          })
          .finally(() => {
            this.setProperties({
              searching: false,
              loading: false,
            });
          });
        break;
      case SEARCH_TYPE_USERS:
        userSearch({ term: searchTerm, limit: 20 })
          .then(async (results) => {
            const model = (await translateResults({ users: results })) || {};
            this.set("model", model);
          })
          .finally(() => {
            this.setProperties({
              searching: false,
              loading: false,
            });
          });
        break;
      default:
        if (this.currentUser) {
          updateRecentSearches(this.currentUser, searchTerm);
        }
        ajax("/search", { data: args })
          .then(async (results) => {
            const model = (await translateResults(results)) || {};

            if (results.grouped_search_result) {
              this.set("q", results.grouped_search_result.term);
            }

            if (args.page > 1) {
              if (model) {
                this.model.set("posts", this.model.posts.concat(model.posts));
                this.model.set(
                  "topics",
                  this.model.topics.concat(model.topics)
                );
                this.model.set(
                  "grouped_search_result",
                  results.grouped_search_result
                );
              }
            } else {
              setTransient("lastSearch", { searchKey, model }, 5);
              model.grouped_search_result = results.grouped_search_result;
              this.set("model", model);
            }
            this.set("error", null);
          })
          .catch((e) => {
            this.set("error", e.jqXHR.responseJSON?.message);
          })
          .finally(() => {
            this.setProperties({
              searching: false,
              loading: false,
            });
            this.appEvents.trigger("search:search_result_view", {
              page: args.page,
            });
          });
        break;
    }
  }

  _afterTransition() {
    if (Object.keys(this.model).length === 0) {
      this.reset();
    }
  }

  reset() {
    this.setProperties({
      searching: false,
      page: 1,
      resultCount: null,
    });
    this.bulkSelectHelper.clearAll();
  }

  @action
  afterBulkActionComplete() {
    return Promise.resolve(this._search());
  }

  @action
  createTopic(searchTerm, event) {
    event?.preventDefault();
    let topicCategory;
    if (searchTerm.includes("category:")) {
      const match = searchTerm.match(/category:(\S*)/);
      if (match && match[1]) {
        topicCategory = match[1];
      }
    }
    this.composer.open({
      action: Composer.CREATE_TOPIC,
      draftKey: Composer.NEW_TOPIC_KEY,
      topicCategory,
    });
  }

  @action
  addSearchResults(list, identifier) {
    this.set("additionalSearchResults", {
      list,
      identifier,
    });
  }

  @action
  setSortOrder(value) {
    this.set("sortOrder", value);
    this.searchPreferencesManager.sortOrder = value;
  }

  @action
  selectAll() {
    addUniqueValuesToArray(
      this.bulkSelectHelper.selected,
      this.searchResultPosts.map((item) => item)
    );

    // Doing this the proper way is a HUGE pain,
    // we can hack this to work by observing each on the array
    // in the component, however, when we select ANYTHING, we would force
    // 50 traversals of the list
    // This hack is cheap and easy
    document
      .querySelectorAll(".fps-result input[type=checkbox]")
      .forEach((checkbox) => {
        checkbox.checked = true;
      });
  }

  @action
  clearAll() {
    this.bulkSelectHelper.clearAll();

    document
      .querySelectorAll(".fps-result input[type=checkbox]")
      .forEach((checkbox) => {
        checkbox.checked = false;
      });
  }

  @action
  toggleBulkSelect() {
    this.toggleProperty("bulkSelectEnabled");
    this.bulkSelectHelper.clearAll();
  }

  @action
  search(options = {}) {
    if (this.searching) {
      return;
    }

    if (options.collapseFilters) {
      this.appEvents.trigger("full-page-search:collapse-filters");
    }
    this.set("page", 1);

    this.appEvents.trigger("full-page-search:trigger-search");

    this._search();
  }

  get canLoadMore() {
    return (
      this.get("model.grouped_search_result.more_full_page_results") &&
      !this.loading &&
      this.page < PAGE_LIMIT
    );
  }

  @action
  loadMore() {
    if (!this.canLoadMore) {
      return;
    }

    applyBehaviorTransformer("full-page-search-load-more", () => {
      this.incrementProperty("page");
      this._search();
    });
  }

  @action
  logClick(topicId) {
    if (this.get("model.grouped_search_result.search_log_id") && topicId) {
      logSearchLinkClick({
        searchLogId: this.get("model.grouped_search_result.search_log_id"),
        searchResultId: topicId,
        searchResultType: "topic",
      });
    }
  }
}
